// Domeniul Orbit CRM. Toate id-urile sunt string-uri stabile (folosite si in data-ai-id).

export type Industry =
  | "logistics"
  | "retail"
  | "fintech"
  | "healthcare"
  | "manufacturing"
  | "travel"
  | "e-commerce"
  | "professional-services";

export type CompanySize = "1-10" | "11-50" | "51-200" | "201-500" | "500+";

export type CompanyStatus = "Prospect" | "Active Customer" | "At Risk" | "Churned";

export interface Company {
  id: string;
  name: string;
  industry: Industry;
  size: CompanySize;
  country: string;
  accountOwner: string;
  status: CompanyStatus;
  createdAt: string; // ISO
  notes?: string;
}

export type InfluenceLevel = "Low" | "Medium" | "High";

export interface Contact {
  id: string;
  companyId: string;
  name: string;
  role: string;
  email: string;
  phone: string;
  influenceLevel: InfluenceLevel;
  decisionMaker: boolean;
}

export type Stage =
  | "New Lead"
  | "Qualified"
  | "Proposal Sent"
  | "Negotiation"
  | "Won"
  | "Lost";

export const PIPELINE_STAGES: Stage[] = [
  "New Lead",
  "Qualified",
  "Proposal Sent",
  "Negotiation",
  "Won",
  "Lost",
];

// Ordinea de avans pentru verificarea "overdue blocheaza mutarea intr-un stage superior".
export const STAGE_RANK: Record<Stage, number> = {
  "New Lead": 0,
  Qualified: 1,
  "Proposal Sent": 2,
  Negotiation: 3,
  Won: 4,
  Lost: -1,
};

export type Priority = "Low" | "Medium" | "High";

export type LeadSource = "Referral" | "Website" | "Outbound" | "Event" | "Partner";

export type LostReason = "Price" | "Competitor" | "No budget" | "No response" | "Timing" | "Other";

export interface Product {
  id: string;
  name: string;
  description: string;
}

export const PRODUCT_CATALOG: Product[] = [
  { id: "starter", name: "Starter Plan", description: "Entry-level package, up to 10 users" },
  { id: "growth", name: "Growth Plan", description: "Mid-tier package, automations included" },
  { id: "enterprise", name: "Enterprise Plan", description: "Full package, dedicated SLA" },
  { id: "integration", name: "Onboarding & Integration", description: "Custom implementation service" },
];

export interface Opportunity {
  id: string;
  name: string;
  companyId: string;
  primaryContactId?: string;
  stage: Stage;
  value?: number; // EUR, estimat
  probability: number; // 0-100
  salesOwner: string;
  expectedCloseDate?: string; // ISO date
  createdAt: string; // ISO
  priority: Priority;
  source: LeadSource;
  productId?: string;
  competitor?: string;
  tags: string[];
  description: string;
  internalNotes?: string;
  lostReason?: LostReason;
  finalValue?: number;
  contractStartDate?: string; // ISO date
}

export type ActivityType =
  | "call"
  | "email"
  | "meeting"
  | "note"
  | "task"
  | "proposal-sent"
  | "stage-changed"
  | "lost-reason"
  | "contract-confirmed";

export interface Activity {
  id: string;
  opportunityId: string;
  type: ActivityType;
  timestamp: string; // ISO
  author: string;
  content: string;
}

export type ProposalStatus = "Draft" | "Sent" | "Viewed" | "Accepted" | "Rejected" | "Expired";

export interface Proposal {
  id: string;
  opportunityId: string;
  proposalNumber: string;
  amount: number;
  discountPercent: number;
  expirationDate: string; // ISO date
  status: ProposalStatus;
  generatedDate: string; // ISO date
  sentDate?: string;
  approved?: boolean; // aprobare manageriala pentru discount > 20%
}

// ---------- Billing ----------

// "Overdue" nu se stocheaza niciodata — se deriva dintr-o factura Sent/Partially
// Paid cu data scadenta trecuta si sold > 0 (vezi effectiveInvoiceStatus).
export type InvoiceStatus = "Draft" | "Sent" | "Partially Paid" | "Paid" | "Overdue" | "Void";

export interface InvoiceLineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  companyId: string;
  opportunityId?: string; // deal-ul Won din care a fost generata (daca e cazul)
  issueDate: string; // ISO date
  dueDate: string; // ISO date
  lineItems: InvoiceLineItem[];
  taxRate: number; // procent, ex. 19
  status: Exclude<InvoiceStatus, "Overdue">;
  amountPaid: number;
  paidDate?: string;
  notes?: string;
}

export const DEFAULT_TAX_RATE = 19;
export const PAYMENT_TERMS_DAYS = 30;

export interface CrmData {
  companies: Company[];
  contacts: Contact[];
  opportunities: Opportunity[];
  activities: Activity[];
  proposals: Proposal[];
  invoices: Invoice[];
}

export const SALES_OWNERS = ["Ana Petrescu", "Bogdan Ionescu", "Cristina Marin", "David Stanescu"] as const;
